// Shared client-communication dictionary + `t()`.
//
// One module for two runtimes: the edge functions import it natively under
// Deno, the frontend imports it by relative path under Vite (the same way
// EmailTemplatesSettings imports `_shared/email-shell`). Nothing in here may
// touch a Deno-only API — `deno check` and `npm run build` both have to pass.
//
// Two interpolation systems, deliberately separate (docs/i18n/plan.md §2):
//   * `t(lang, key, vars)` for UI/PDF strings uses single-brace `{name}`.
//   * Email template defaults keep the `{{customer_name}}` contract and are
//     substituted by the allowlisted, escaped path in email-shell.ts. `t()`
//     never touches a double-brace token, so `email.*` strings pass through
//     verbatim.
// Plurals are explicit `_one` / `_other` keys chosen by `plural()`. No ICU.

import { en } from './en/index.ts';
import { es } from './es/index.ts';
import { fr } from './fr/index.ts';
import { type Lang, DEFAULT_LANGUAGE, localeTag, normalizeLanguage } from './locale.ts';

export * from './locale.ts';

export type DictionaryValue = string | { [key: string]: DictionaryValue };
export type Dictionary = { [key: string]: DictionaryValue };

export const NAMESPACES = [
  'common', 'pdf', 'email', 'proposal', 'esign', 'invoice', 'pay', 'portal',
  'lead', 'mockup', 'doordrop', 'contract', 'signoff',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const DICTIONARIES: Record<Lang, Dictionary> = { en, es, fr };

/** Keys reserved for translator notes; never returned by `t()`, never a leaf. */
export const CONTEXT_KEY = '_context';

export type TranslationVars = Record<string, string | number | boolean | null | undefined>;

function lookup(dict: Dictionary, key: string): string | undefined {
  const parts = key.split('.');
  let node: DictionaryValue | undefined = dict;
  for (const part of parts) {
    if (part === CONTEXT_KEY) return undefined;
    if (node === undefined || typeof node === 'string') return undefined;
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
}

/**
 * `{name}` → vars.name. A double-brace `{{name}}` (the email contract) is
 * left alone, and an unknown/undefined var keeps its token so a missing value
 * is visible rather than silently blank.
 */
export function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) return template;
  // No lookbehind: Safari before iOS 16.4 / macOS 13.3 throws a SyntaxError
  // ("invalid group specifier name") the moment it PARSES one, which took the
  // public lead form down on an iPhone running iOS 15 (Sentry
  // LIGHTMASTER-WEB-38, 2026-09-28). The "not preceded by {" half of the old
  // pattern is checked by hand on the match offset instead.
  return template.replace(/\{([A-Za-z0-9_]+)\}(?!\})/g, (token, name: string, offset: number, whole: string) => {
    if (offset > 0 && whole[offset - 1] === '{') return token;
    const value = vars[name];
    return value === undefined || value === null ? token : String(value);
  });
}

// --- marker mode -----------------------------------------------------------
// The untranslated-string sweep (plan §7.4c) runs every public route with
// I18N_MARK=1: `t()` then returns «key» so anything still in English is a
// hardcoded string, not a dictionary miss. Read once, overridable in tests.
type DenoLike = { env?: { get?: (key: string) => string | undefined } };
type BundlerMeta = { env?: Record<string, string | undefined> };

function readMarkerFlag(): boolean {
  try {
    const deno = (globalThis as { Deno?: DenoLike }).Deno;
    if (deno?.env?.get?.('I18N_MARK') === '1') return true;
  } catch { /* no env access — fine */ }
  try {
    const env = (import.meta as unknown as BundlerMeta).env;
    if (env?.VITE_I18N_MARK === '1') return true;
  } catch { /* not a bundler runtime */ }
  return false;
}
let markerMode = readMarkerFlag();
export function setMarkerMode(on: boolean): void {
  markerMode = on;
}

// --- missing-key warnings, once per (language, key) ------------------------
const warned = new Set<string>();
function warnOnce(lang: Lang, key: string, englishExists: boolean): void {
  const id = `${lang}:${key}`;
  if (warned.has(id)) return;
  warned.add(id);
  if (englishExists) {
    console.warn(`[i18n] missing ${lang} translation for "${key}" — using English`);
  } else {
    console.warn(`[i18n] unknown translation key "${key}"`);
  }
}
/** Test hook: forget which keys already warned. */
export function resetWarnings(): void {
  warned.clear();
}

export function hasKey(lang: unknown, key: string): boolean {
  return lookup(DICTIONARIES[normalizeLanguage(lang)], key) !== undefined;
}

/**
 * Translate `key` ("namespace.path.to.leaf") for `lang`. A missing key falls
 * back to English with ONE console warning per (language, key); a key missing
 * in English too returns the key itself so the gap is visible on screen.
 */
export function t(lang: unknown, key: string, vars?: TranslationVars): string {
  const language = normalizeLanguage(lang);
  if (markerMode) return `«${key}»`;
  const hit = lookup(DICTIONARIES[language], key);
  if (hit !== undefined) return interpolate(hit, vars);
  const fallback = lookup(DICTIONARIES[DEFAULT_LANGUAGE], key);
  warnOnce(language, key, fallback !== undefined);
  return fallback !== undefined ? interpolate(fallback, vars) : key;
}

/**
 * Cardinal plural: picks `${key}_one` or `${key}_other` using the locale's
 * own rule (French counts 0 as singular — "0 jour"; English and Spanish do
 * not), and exposes `{count}` to the template.
 */
export function plural(lang: unknown, key: string, count: number, vars?: TranslationVars): string {
  const category = new Intl.PluralRules(localeTag(lang)).select(count);
  const suffix = category === 'one' ? '_one' : '_other';
  return t(lang, `${key}${suffix}`, { count, ...vars });
}

/** Bound translator for a fixed language — handy for a render pass. */
export function translator(lang: unknown) {
  const language = normalizeLanguage(lang);
  return {
    lang: language,
    t: (key: string, vars?: TranslationVars) => t(language, key, vars),
    plural: (key: string, count: number, vars?: TranslationVars) => plural(language, key, count, vars),
  };
}
